import { useEffect, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { getUsers } from '../../services/api';

function AdminMembersList() {
  const [members, setMembers] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [flash, setFlash] = useState('');
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const load = async () => {
      try {
        setLoading(true);
        setError('');
        const data = await getUsers();
        setMembers(data.results || data);
      } catch (e) {
        setError(e.message || 'Failed to load members');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  useEffect(() => {
    if (location.state?.success) {
      setFlash(location.state.success);
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [location, navigate]);

  const filteredMembers = members.filter((member) => {
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    const fullName = `${member.first_name} ${member.last_name}`.toLowerCase();
    const email = (member.email || '').toLowerCase();
    return fullName.includes(query) || email.includes(query);
  });

  const formatDate = (dateString) => {
    if (!dateString) return '—';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
  };

  const getMembershipTypeName = (member) => {
    if (member.membership && member.membership.membership_type) {
      return member.membership.membership_type.name;
    }
    return '—';
  };

  return (
    <div className="p-4">
      <div className="flex items-center justify-between mb-4">
        <h1 className="text-2xl font-bold">Admin • Members</h1>
      </div>

      <div className="mb-4">
        <input
          type="text"
          placeholder="Search by name or email..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="w-full md:w-96 px-4 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
        />
      </div>

      {flash && <div className="text-green-600 mb-3">{flash}</div>}
      {error && <div className="text-red-600 mb-3">{error}</div>}

      {loading ? (
        <div>Loading...</div>
      ) : (
        <div className="overflow-auto bg-white rounded shadow">
          <table className="min-w-full text-sm">
            <thead className="bg-gray-50 text-left">
              <tr>
                <th className="px-4 py-2">Name</th>
                <th className="px-4 py-2">Email</th>
                <th className="px-4 py-2">Membership</th>
                <th className="px-4 py-2">Active</th>
                <th className="px-4 py-2">Date Joined</th>
                <th className="px-4 py-2">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredMembers.length === 0 ? (
                <tr>
                  <td colSpan="6" className="px-4 py-8 text-center text-gray-500">
                    {searchQuery ? 'No members found matching your search.' : 'No members found.'}
                  </td>
                </tr>
              ) : (
                filteredMembers.map((member) => (
                  <tr key={member.id} className="border-t border-gray-200 hover:bg-gray-50">
                    <td className="px-4 py-3">
                      {member.first_name} {member.last_name}
                    </td>
                    <td className="px-4 py-3">{member.email}</td>
                    <td className="px-4 py-3">{getMembershipTypeName(member)}</td>
                    <td className="px-4 py-3">
                      {member.is_active ? (
                        <span className="text-green-600">✓ Yes</span>
                      ) : (
                        <span className="text-red-600">✗ No</span>
                      )}
                    </td>
                    <td className="px-4 py-3">{formatDate(member.date_joined)}</td>
                    <td className="px-4 py-3">
                      <Link
                        to={`/admin/members/${member.id}`}
                        className="text-blue-600 hover:text-blue-800 underline"
                      >
                        Edit
                      </Link>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      )}

      {!loading && filteredMembers.length > 0 && (
        <div className="mt-4 text-sm text-gray-600">
          Showing {filteredMembers.length} of {members.length} member{members.length !== 1 ? 's' : ''}
        </div>
      )}
    </div>
  );
}

export default AdminMembersList;
